import { AlertTriangle, CheckCircle2, LoaderCircle, X } from "lucide-react";
import type { AiReprocessItem, AiReprocessRun } from "@/shared/api/api-client";
import { OUTCOME_LABELS, isAiReprocessRunning } from "@/features/mentions/ai-reprocess-view";
import { AiReprocessProgress } from "@/features/mentions/AiReprocessProgress";

/**
 * O andamento e o desfecho de um reprocessamento por IA, na tela de
 * menções — mesma forma do banner do lote: rodando (contador), terminado
 * (alteradas, sem mudança, puladas, com erro — os motivos sob demanda)
 * e errado (a mensagem do servidor).
 */
export function AiReprocessBanner({
  run,
  issues,
  onShowIssues,
  onDismiss,
  onOpenDetail
}: {
  run: AiReprocessRun;
  issues: AiReprocessItem[] | null;
  onShowIssues: () => void;
  onDismiss: () => void;
  /** Abre a execução em `/reprocessamentos/:id`, onde o andamento fica
   *  visível de qualquer lugar e com o detalhe por desfecho. */
  onOpenDetail?: () => void;
}) {
  const { counters } = run;
  const done = counters.total - counters.pending;
  const total = counters.total.toLocaleString("pt-BR");
  const detailLink = onOpenDetail && (
    <button type="button" className="text-button" onClick={onOpenDetail}>
      Ver andamento
    </button>
  );

  if (isAiReprocessRunning(run)) {
    return (
      <section className="card bulk-banner" aria-live="polite">
        <p className="bulk-banner-head">
          <LoaderCircle className="spin" size={16} aria-hidden="true" />
          Reprocessando {total} menções com IA…
        </p>
        {detailLink && <div className="bulk-banner-actions">{detailLink}</div>}
        <AiReprocessProgress run={run} label="Reprocessamento" />
      </section>
    );
  }

  if (run.status === "ERRORED" && run.retrying) {
    return (
      <section className="card bulk-banner" aria-live="polite">
        <p className="bulk-banner-head">
          <LoaderCircle className="spin" size={16} aria-hidden="true" />
          O reprocessamento encontrou um erro e vai tentar de novo sozinho…
        </p>
        {detailLink && <div className="bulk-banner-actions">{detailLink}</div>}
        <AiReprocessProgress run={run} label="Reprocessamento" />
      </section>
    );
  }

  if (run.status === "ERRORED") {
    return (
      <section className="card bulk-banner is-error" aria-live="polite">
        <p className="bulk-banner-head">
          <AlertTriangle size={16} aria-hidden="true" />
          O reprocessamento parou com erro{run.message ? `: ${run.message}` : "."}{" "}
          {done > 0 && `${done.toLocaleString("pt-BR")} de ${total} menções foram processadas antes.`}
        </p>
        <div className="bulk-banner-actions">
          {detailLink}
          <button type="button" className="icon-button" onClick={onDismiss} aria-label="Fechar aviso">
            <X size={16} />
          </button>
        </div>
        {done > 0 && <AiReprocessProgress run={run} label="Reprocessamento" />}
      </section>
    );
  }

  const problems = counters.skipped + counters.failed;
  return (
    <section
      className={`card bulk-banner ${counters.failed ? "has-failures" : "is-done"}`}
      aria-live="polite"
    >
      <p className="bulk-banner-head">
        <CheckCircle2 size={16} aria-hidden="true" />
        Reprocessamento concluído
      </p>
      <div className="bulk-banner-actions">
        {detailLink}
        {problems > 0 && issues === null && (
          <button type="button" className="text-button" onClick={onShowIssues}>
            Ver puladas e erros
          </button>
        )}
        <button type="button" className="icon-button" onClick={onDismiss} aria-label="Fechar aviso">
          <X size={16} />
        </button>
      </div>
      <AiReprocessProgress run={run} label="Reprocessamento" />
      {issues && issues.length > 0 && (
        <ul className="bulk-failures">
          {issues.map(item => (
            <li key={item.mention_id}>
              Menção #{item.mention_id}: {item.outcome ? OUTCOME_LABELS[item.outcome] : "pendente"}
              {item.message ? ` — ${item.message}` : ""}
            </li>
          ))}
          {problems > issues.length && <li>… e mais {problems - issues.length}.</li>}
        </ul>
      )}
    </section>
  );
}
